import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { useMasterData } from '@/hooks/useMasterData';
import type { ScheduleRegistrationFormData } from '@/types/schedule';
import type { LandTransporter, BusinessPartner } from '@/types';

// バリデーションメッセージ定数
const VALIDATION_MESSAGES = {
  CHASSIS_NUMBER_REQUIRED: '車体番号の入力が必要です',
  CHASSIS_NUMBER_INVALID_FORMAT: '車体番号は英数字とハイフンのみ使用できます',
} as const;

/**
 * BusinessPartnerの表示名を取得するヘルパー関数
 * displayNameが存在する場合はそれを使用し、なければnameを使用
 */
const getPartnerDisplayName = (partner: BusinessPartner): string => {
  return partner.displayName || partner.name;
};

/**
 * 車体番号のフォーマットを検証
 * 英数字（A-Z, 0-9）とハイフン（-）のみ許可
 */
const validateChassisNumberFormat = (value: string): boolean => {
  return /^[A-Z0-9-]+$/i.test(value);
};

/**
 * 入力値をサニタイゼーション
 * HTML特殊文字をエスケープしてXSS攻撃を防止
 */
const sanitizeInput = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * スケジュール直接登録モーダルのプロパティ
 */
export interface ScheduleDirectRegistrationModalProps {
  /** モーダルが開いているかどうか */
  isOpen: boolean;
  /** 選択された陸送員のID */
  transporterId: string;
  /** スケジュールの日付 (YYYY-MM-DD形式) */
  date: string;
  /** スケジュールの開始時刻 (HH:mm形式) */
  timeStart: string;
  /** 利用可能な陸送員のリスト（オプション） */
  transporters?: LandTransporter[];
  /** 登録保存時のコールバック */
  onSave: (data: ScheduleRegistrationFormData) => void;
  /** モーダルキャンセル/クローズ時のコールバック */
  onCancel: () => void;
}

/**
 * ScheduleDirectRegistrationModal
 *
 * スケジュール画面から直接陸送案件を登録するためのモーダルコンポーネント。
 * ユーザーがスケジュール詳細を入力し、登録またはキャンセルできる。
 *
 * ## 主な機能
 * - 受付ステータス選択（仮受/本受）
 * - 取引先情報入力（請求先、集荷先、納車先）
 * - 車両情報入力（車両モデル、ナンバープレート）
 * - 本受時の車体番号必須バリデーション
 * - 備考入力
 *
 * @example
 * ```tsx
 * <ScheduleDirectRegistrationModal
 *   isOpen={isModalOpen}
 *   transporterId={selectedTransporterId}
 *   date={selectedDate}
 *   timeStart={selectedTime}
 *   transporters={transporters}
 *   onSave={handleSave}
 *   onCancel={handleCancel}
 * />
 * ```
 */
export const ScheduleDirectRegistrationModal: React.FC<ScheduleDirectRegistrationModalProps> = ({
  isOpen,
  transporterId,
  date,
  timeStart,
  transporters: transportersProp,
  onSave,
  onCancel,
}) => {
  const {
    landTransporters: transportersFromHook,
    businessPartners,
    vehicleModels,
  } = useMasterData();
  const transporters = transportersProp ?? transportersFromHook;

  // デフォルト値（undefined時の安全性確保）
  const safeBusinessPartners = businessPartners ?? [];
  const safeVehicleModels = vehicleModels ?? [];

  // 受付ステータス: 'provisional' (仮受) or 'confirmed' (本受)
  const [acceptanceStatus, setAcceptanceStatus] = useState<'provisional' | 'confirmed'>(
    'provisional'
  );

  // フォーム状態
  const [billingPartnerId, setBillingPartnerId] = useState('');
  const [pickupPartnerId, setPickupPartnerId] = useState('');
  const [deliveryPartnerId, setDeliveryPartnerId] = useState('');
  const [vehicleModelId, setVehicleModelId] = useState('');
  const [vehiclePlateNumber, setVehiclePlateNumber] = useState('');
  const [chassisNumber, setChassisNumber] = useState('');
  const [remarks, setRemarks] = useState('');

  // エラー状態
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // ローディング状態
  const [isLoading, setIsLoading] = useState(false);

  // 一般エラー状態
  const [generalError, setGeneralError] = useState<string | null>(null);

  // 陸送員情報取得
  const transporter = transporters.find((t: LandTransporter) => t.id === transporterId);
  const transporterName = transporter?.name || '不明';

  // モーダルが開いた時にフォームをリセット
  useEffect(() => {
    if (isOpen) {
      setAcceptanceStatus('provisional');
      setBillingPartnerId('');
      setPickupPartnerId('');
      setDeliveryPartnerId('');
      setVehicleModelId('');
      setVehiclePlateNumber('');
      setChassisNumber('');
      setRemarks('');
      setErrors({});
      setIsLoading(false);
      setGeneralError(null);
    }
  }, [isOpen]);

  // バリデーション
  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // 本受モードでは車体番号必須
    if (acceptanceStatus === 'confirmed') {
      const trimmedChassisNumber = chassisNumber.trim();
      if (!trimmedChassisNumber) {
        newErrors.chassisNumber = VALIDATION_MESSAGES.CHASSIS_NUMBER_REQUIRED;
      } else if (!validateChassisNumberFormat(trimmedChassisNumber)) {
        newErrors.chassisNumber = VALIDATION_MESSAGES.CHASSIS_NUMBER_INVALID_FORMAT;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 保存処理
  const handleSave = async () => {
    // バリデーションチェック
    if (!validate()) {
      return;
    }

    try {
      setIsLoading(true);
      setGeneralError(null);

      // 入力値をサニタイゼーション
      const sanitizedChassisNumber = chassisNumber ? sanitizeInput(chassisNumber.trim()) : '';
      const sanitizedVehiclePlateNumber = vehiclePlateNumber
        ? sanitizeInput(vehiclePlateNumber.trim())
        : '';
      const sanitizedRemarks = remarks ? sanitizeInput(remarks.trim()) : '';

      const formData: ScheduleRegistrationFormData = {
        transporterId,
        date,
        timeStart,
        acceptanceStatus,
        billingPartnerId: billingPartnerId || undefined,
        pickupPartnerId: pickupPartnerId || undefined,
        deliveryPartnerId: deliveryPartnerId || undefined,
        vehicleModelId: vehicleModelId || undefined,
        vehiclePlateNumber: sanitizedVehiclePlateNumber || undefined,
        chassisNumber: sanitizedChassisNumber || undefined,
        remarks: sanitizedRemarks || undefined,
      };

      await onSave(formData);
    } catch (error) {
      console.error('案件登録エラー:', error);
      setGeneralError(
        error instanceof Error
          ? error.message
          : '案件の登録に失敗しました。ネットワーク接続を確認して再度お試しください。'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title='スケジュール直接登録'>
      <div className='space-y-6'>
        {/* 陸送員・日時情報 */}
        <div className='rounded-lg bg-gray-50 p-4 dark:bg-gray-800'>
          <div className='grid grid-cols-3 gap-4 text-sm'>
            <div>
              <span className='font-medium text-gray-700 dark:text-gray-300'>陸送員:</span>
              <span className='ml-2 text-gray-900 dark:text-white'>{transporterName}</span>
            </div>
            <div>
              <span className='font-medium text-gray-700 dark:text-gray-300'>日付:</span>
              <span className='ml-2 text-gray-900 dark:text-white'>{date}</span>
            </div>
            <div>
              <span className='font-medium text-gray-700 dark:text-gray-300'>開始時刻:</span>
              <span className='ml-2 text-gray-900 dark:text-white'>{timeStart}</span>
            </div>
          </div>
        </div>

        {/* 受付ステータス切替 */}
        <div className='space-y-2'>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
            受付ステータス
          </label>
          <div className='flex gap-2'>
            <button
              type='button'
              role='button'
              aria-pressed={acceptanceStatus === 'provisional'}
              onClick={() => setAcceptanceStatus('provisional')}
              className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                acceptanceStatus === 'provisional'
                  ? 'bg-yellow-100 text-yellow-900 ring-2 ring-yellow-500 dark:bg-yellow-900/40 dark:text-yellow-100'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              📋 仮受
            </button>
            <button
              type='button'
              role='button'
              aria-pressed={acceptanceStatus === 'confirmed'}
              onClick={() => setAcceptanceStatus('confirmed')}
              className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                acceptanceStatus === 'confirmed'
                  ? 'bg-blue-100 text-blue-900 ring-2 ring-blue-500 dark:bg-blue-900/40 dark:text-blue-100'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              ✓ 本受
            </button>
          </div>
          <p className='text-xs text-gray-500 dark:text-gray-400'>
            {acceptanceStatus === 'provisional'
              ? '電話・LINE相談段階、車体番号未確定'
              : '正式依頼書受領後、車体番号入力済み'}
          </p>
        </div>

        {/* 取引先情報 */}
        <div className='grid grid-cols-3 gap-4'>
          <div>
            <label
              htmlFor='billing-partner'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              請求先 <span className='text-gray-500'>(任意)</span>
            </label>
            <select
              id='billing-partner'
              value={billingPartnerId}
              onChange={e => setBillingPartnerId(e.target.value)}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm'
            >
              <option value=''>選択してください</option>
              {safeBusinessPartners.map(partner => (
                <option key={partner.id} value={partner.id}>
                  {getPartnerDisplayName(partner)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor='pickup-partner'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              集荷先 <span className='text-gray-500'>(任意)</span>
            </label>
            <select
              id='pickup-partner'
              value={pickupPartnerId}
              onChange={e => setPickupPartnerId(e.target.value)}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm'
            >
              <option value=''>選択してください</option>
              {safeBusinessPartners.map(partner => (
                <option key={partner.id} value={partner.id}>
                  {getPartnerDisplayName(partner)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor='delivery-partner'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              納車先 <span className='text-gray-500'>(任意)</span>
            </label>
            <select
              id='delivery-partner'
              value={deliveryPartnerId}
              onChange={e => setDeliveryPartnerId(e.target.value)}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm'
            >
              <option value=''>選択してください</option>
              {safeBusinessPartners.map(partner => (
                <option key={partner.id} value={partner.id}>
                  {getPartnerDisplayName(partner)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 車両情報 */}
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label
              htmlFor='vehicle-model'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              車両モデル <span className='text-gray-500'>(任意)</span>
            </label>
            <select
              id='vehicle-model'
              value={vehicleModelId}
              onChange={e => setVehicleModelId(e.target.value)}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm'
            >
              <option value=''>選択してください</option>
              {safeVehicleModels.map(model => (
                <option key={model.id} value={model.id}>
                  {model.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor='plate-number'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              ナンバープレート <span className='text-gray-500'>(任意)</span>
            </label>
            <input
              type='text'
              id='plate-number'
              value={vehiclePlateNumber}
              onChange={e => setVehiclePlateNumber(e.target.value)}
              placeholder='品川 500 あ 1234'
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm'
            />
          </div>
        </div>

        {/* 本受時のみ: 車体番号 */}
        {acceptanceStatus === 'confirmed' && (
          <div>
            <label
              htmlFor='chassis-number'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              車体番号 <span className='text-red-600'>*</span>
            </label>
            <input
              type='text'
              id='chassis-number'
              value={chassisNumber}
              onChange={e => setChassisNumber(e.target.value)}
              placeholder='CHASSIS-12345'
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm ${
                errors.chassisNumber ? 'border-red-500' : ''
              }`}
            />
            {errors.chassisNumber && (
              <p className='mt-1 text-sm text-red-600'>{errors.chassisNumber}</p>
            )}
          </div>
        )}

        {/* 備考 */}
        <div>
          <label
            htmlFor='remarks'
            className='block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            備考 <span className='text-gray-500'>(任意)</span>
          </label>
          <textarea
            id='remarks'
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            rows={3}
            className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm'
            placeholder='特記事項があれば入力してください'
          />
        </div>

        {/* 一般エラーメッセージ */}
        {generalError && (
          <div
            className='rounded-md bg-red-50 p-4 dark:bg-red-900/20'
            role='alert'
            aria-live='polite'
          >
            <div className='flex'>
              <div className='flex-shrink-0'>
                <svg
                  className='h-5 w-5 text-red-400'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                  aria-hidden='true'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <p className='text-sm font-medium text-red-800 dark:text-red-200'>{generalError}</p>
              </div>
            </div>
          </div>
        )}

        {/* ボタン */}
        <div className='flex justify-end gap-3 border-t pt-4 dark:border-gray-700'>
          <button
            type='button'
            onClick={onCancel}
            disabled={isLoading}
            className='rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          >
            キャンセル
          </button>
          <button
            type='button'
            onClick={handleSave}
            disabled={isLoading}
            className='inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600'
          >
            {isLoading ? (
              <>
                <svg
                  className='-ml-1 mr-2 h-4 w-4 animate-spin text-white'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
                登録中...
              </>
            ) : (
              '登録'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
